import { useState, useContext, useEffect, useMemo, SetStateAction, Dispatch, FormEvent } from "react";
import Busy from "../../Busy";
import moment from 'moment';
import { styled } from "@mui/material/styles";
import PhoneInput from "react-phone-number-input/input";
import { requestLocationSpace } from "../../../requests/new-location-requests";
import { useSnackbar } from "../../../context/snackbar-context";
import { getErrorMessageForNonStandardAndStandardResponse } from "../../../util/NetworkErrorUtil";
import { AppContext } from "../../../context/app-context";
import { useLocationSearchParams } from "../../../hooks/search/useLocationSearchParams";
import { isLoggedIn } from "../../../util/AccountUtils";
import { PhoneNumberTextField } from "../../form/PhoneNumberTextField";
import CityStateAutocomplete from "./CityStateAutocomplete";
import { getQueryParamsFromPacItem } from "../../../util/AddressUtils";
import { PlacePrediction } from "../../placesAutocomplete/interfaces";
import {DatePicker, LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment'
import { useGetOptions } from "../../../hooks/report/useGetOptions";
import EquipmentTypes from "../../../components/EquipmentTypes";
import {
  Button,
  Box,
  Grid,
  Paper,
  TextField,
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  //SelectChangeEvent,
  InputLabel,
  Checkbox,
  ListItemText,
  Typography
} from "@mui/material";
import {
  validatePhoneNumber,
  validateEmail,
  parseNumericInput,
  validatePresentOrFutureDate
} from "../../../util/ValidationUtils";
import {
  BookingDurationType,
  DateFormats
} from "../../../components/constants/securspace-constants";

const RequestLocationFormCard = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: 640,
  margin: '0 auto',
  padding: theme.spacing(4),
  borderRadius: 12,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 2),
  },
}));

interface RequestLocationField {
  label: string;
  name: string;
  errorMessage: string;
  validator: (value: string) => boolean;
  formatter?: (value: string) => string;
}

interface RequestLocationFields {
  [key: string]: RequestLocationField
}

const FIELDS: RequestLocationFields = Object.freeze({
  FIRST_NAME:         {label: 'First Name', name: 'firstName', errorMessage: 'Please enter your first name', validator: (value: string) => !!value},
  LAST_NAME:          {label: 'Last Name', name: 'lastName', errorMessage: 'Please enter your last name', validator: (value: string) => !!value},
  PHONE_NUMBER:       {label: 'Phone Number', name: 'phoneNumber', errorMessage: 'Please enter a valid phone number', validator: (value: string) => validatePhoneNumber(value, true)},
  EMAIL:              {label: 'Email', name: 'email', errorMessage: 'Please enter a valid email address', validator: (value: string) => validateEmail(value)},
  NUMBER_OF_SPACES:   {label: 'Number Of Spaces', name: 'numberOfSpaces', errorMessage: 'Number must be greater than zero', validator: (value: string) => !!value && Number(value) > 0, formatter: (value: string) => parseNumericInput(value)},
  START_DATE:         {label: 'Start Date', name: 'startDate', errorMessage: 'Please enter a future start date', validator: (value: string) => validatePresentOrFutureDate(value)},
  DURATION:           {label: 'Duration', name: 'duration', errorMessage: 'Please select a duration', validator: (value: string) => !!value},
  EQUIPMENT_TYPES:    {label: 'Equipment Type', name: 'equipmentTypes', errorMessage: 'Please select the equipment', validator: (value: string) => (Number(value) > 0)},
})

interface RequestLocationFormProps {
  /** Resolved city for the searched area. Falls back to the URL params when omitted. */
  city?: string;
  /** Resolved state for the searched area. Falls back to the URL params when omitted. */
  state?: string;
  /**
   * When true (coordinate/drag mode — no searched city), the city/state is an
   * editable autocomplete the user fills in. When false (a city was searched /
   * landed on), it's locked to that city so it can't be changed to one that
   * already has locations.
   */
  editable?: boolean;
  /**
   * Search/navigate to the city the user picks in the (editable) city field —
   * the map moves there and, if it has locations, the existing logic hides this
   * form and shows the list.
   */
  onCitySearch?: (pacItem: PlacePrediction) => void;
}

// Normalizes a stored phone number (which may include the "1" country code, e.g.
// "18338757275") into E.164 format ("+18338757275") so react-phone-number-input
// renders it as a national number without surfacing the country code.
const normalizePhoneNumberToE164 = (rawPhoneNumber?: string): string => {
  const digits = (rawPhoneNumber || '').replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  return `+${digits}`;
};

const RequestLocationForm = (props: RequestLocationFormProps) => {
  const [currentLocationParams] = useLocationSearchParams();

  const appContext = useContext(AppContext)
  const user = useMemo(() => appContext.user, [appContext]);
  const durationOptions = useGetOptions({optionFormat: 'SELECT', options: BookingDurationType, includeAllOption: false});
  const equipmentTypeOptions = useGetOptions({optionFormat: 'SELECT', options: EquipmentTypes.getCondensedAssetTypeTaxCodes(), includeAllOption: false});


  const loggedIn = useMemo(() => user && isLoggedIn(user), [user]);

  // Editable city/state. Seeded from the searched area (props, falling back to the
  // URL), but the user can change it via the city autocomplete — required in
  // coordinate mode (after a map drag) where the URL carries only lat/lng.
  const [city, setCity] = useState<string>(props.city || currentLocationParams?.city || '');
  const [state, setState] = useState<string>(props.state || currentLocationParams?.state || '');
  const [cityStateText, setCityStateText] = useState<string>(() => {
    const c = props.city || currentLocationParams?.city || '';
    const s = props.state || currentLocationParams?.state || '';
    return c && s ? `${c}, ${s}` : c;
  });
  const [numberOfSpaces, setNumberOfSpaces] = useState<string>("1");
  const [firstName, setFirstName] = useState<string>(user && loggedIn && user?.loggedInUsername === user?.email ? user?.firstName : '');
  const [lastName, setLastName] = useState<string>(user && loggedIn && user?.loggedInUsername === user?.email ? user?.lastName : '');
  const [email, setEmail] = useState<string>(user && loggedIn ? user?.loggedInUsername : '');
  const [phoneNumber, setPhoneNumber] = useState<string>(user && loggedIn && user?.loggedInUsername === user?.email ? normalizePhoneNumberToE164(user?.phoneNumber) : '');
  const [startDate, setStartDate] = useState<string>('');
  const [duration, setDuration] = useState<string>('');
  const [equipmentTypes, setEquipmentTypes] = useState<string[]>([]);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const {snackbarShowMessage} = useSnackbar();

  const onSubmitForm = () => {

    if(city && state && !disableSubmit && !submitting && !submitted) {
      setSubmitting(true)
      Busy.set(true)
      const request = {
        firstName,
        lastName,
        phone: phoneNumber,
        email,
        city,
        state,
        numberOfSpaces,
        startDate,
        duration,
        equipmentTypes
      }
      requestLocationSpace(request)
        .then(() => {
          setSubmitted(true)
          snackbarShowMessage(`Request for space in ${city} has been sent.  Securspace will be in contact shortly!`, 'success')
        })
        .catch((err: any) => {
          snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err), 'error')
        })
        .finally(() => {
          setSubmitting(false)
          Busy.set(false)
        });
    }
  }

  useEffect(() => {
    setDisableSubmit(!(
            FIELDS.PHONE_NUMBER.validator(phoneNumber) &&
            FIELDS.EMAIL.validator(email) &&
            FIELDS.NUMBER_OF_SPACES.validator(numberOfSpaces) &&
            FIELDS.FIRST_NAME.validator(firstName) &&
            FIELDS.LAST_NAME.validator(lastName) &&
            FIELDS.START_DATE.validator(startDate) &&
            FIELDS.DURATION.validator(duration) &&
            FIELDS.EQUIPMENT_TYPES.validator(String(equipmentTypes ? equipmentTypes.length : 0)) &&
            !!city && !!state
    ));
  }, [firstName, lastName, phoneNumber, email, numberOfSpaces, city, state, startDate, duration, equipmentTypes])

  // Re-seed the editable city/state when the searched area changes (a new
  // no-results search, or coordinate mode clearing it after a map drag).
  useEffect(() => {
    const c = props.city || '';
    const s = props.state || '';
    setCity(c);
    setState(s);
    setCityStateText(c && s ? `${c}, ${s}` : c);
  }, [props.city, props.state])

  // Reset the submitted state when the searched area changes so the form can be
  // submitted again for a different no-results location within the same session
  // (the component instance is reused while the no-results view stays mounted).
  useEffect(() => {
    setSubmitted(false);
  }, [city, state])

  // CHANGE HANDLERS
  const handlePhoneNumberChange = (value?: string) => setPhoneNumber(value || '');

  const handleStartDateChange = (date: moment.Moment | null) => {
    setStartDate(date ? date.format(DateFormats.DAY) : '');
  };

  const constructChangeHandler = (hubspotField: RequestLocationField, setter: Dispatch<SetStateAction<string>> | Dispatch<SetStateAction<string[]>>) => (event: {target: any}) => {
    const {value} = event.target;
    const formattedValue = hubspotField?.formatter ? hubspotField?.formatter(value) : value;
    setter(formattedValue);
  }

  const isValid: (hubspotField: RequestLocationField, formattedValue: string | number) => boolean = (hubspotField: RequestLocationField, formattedValue: string | number) => {
    const stringValue = String(formattedValue);
    return !!stringValue && !hubspotField.validator(stringValue);
  }

  const getHelperText: (hubspotField: RequestLocationField, formattedValue: string | number) => string = (hubspotField: RequestLocationField, formattedValue: string | number) => {
    return hubspotField.validator(String(formattedValue)) ? '' : hubspotField.errorMessage;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmitForm();
  }

  return (
    <RequestLocationFormCard elevation={2}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {props.editable ? (
                <CityStateAutocomplete
                  value={cityStateText}
                  onChange={(text) => { setCityStateText(text); setCity(''); setState(''); }}
                  onCitySelected={(pacItem) => {
                    const params = getQueryParamsFromPacItem(pacItem);
                    if (params?.city && params?.state) {
                      setCity(params.city);
                      setState(params.state);
                      setCityStateText(`${params.city}, ${params.state}`);
                    }
                    // Treat the in-form city pick as a search: move the map to that
                    // city. If it has locations, the existing viewport logic hides
                    // this form and shows the list; otherwise the form re-anchors to it.
                    props.onCitySearch?.(pacItem);
                  }}
                  error={!(city && state)}
                  helperText={!(city && state) ? 'Select a city from the list' : ''}
                />
              ) : (
                <TextField
                  fullWidth
                  value={city && state ? `${city}, ${state}` : ''}
                  label="City, State"
                  name="cityState"
                  disabled
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                value={numberOfSpaces}
                label={FIELDS.NUMBER_OF_SPACES.label}
                name={FIELDS.NUMBER_OF_SPACES.name}
                helperText={getHelperText(FIELDS.NUMBER_OF_SPACES, numberOfSpaces)}
                error={isValid(FIELDS.NUMBER_OF_SPACES, numberOfSpaces)}
                onChange={constructChangeHandler(FIELDS.NUMBER_OF_SPACES, setNumberOfSpaces)}
                inputProps={{ inputMode: 'numeric' }}
                required={true}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                value={firstName}
                label={FIELDS.FIRST_NAME.label}
                name={FIELDS.FIRST_NAME.name}
                helperText={getHelperText(FIELDS.FIRST_NAME, firstName)}
                error={isValid(FIELDS.FIRST_NAME, firstName)}
                onChange={constructChangeHandler(FIELDS.FIRST_NAME, setFirstName)}
                required={true}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                value={lastName}
                label={FIELDS.LAST_NAME.label}
                name={FIELDS.LAST_NAME.name}
                helperText={getHelperText(FIELDS.LAST_NAME, lastName)}
                error={isValid(FIELDS.LAST_NAME, lastName)}
                onChange={constructChangeHandler(FIELDS.LAST_NAME, setLastName)}
                required={true}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <PhoneInput
                fullWidth
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                inputComponent={PhoneNumberTextField}
                defaultCountry="US"
                label={FIELDS.PHONE_NUMBER.label}
                name={FIELDS.PHONE_NUMBER.name}
                helperText={getHelperText(FIELDS.PHONE_NUMBER, phoneNumber)}
                error={isValid(FIELDS.PHONE_NUMBER, phoneNumber)}
                required={true}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                value={email}
                label={FIELDS.EMAIL.label}
                name={FIELDS.EMAIL.name}
                helperText={getHelperText(FIELDS.EMAIL, email)}
                error={isValid(FIELDS.EMAIL, email)}
                onChange={constructChangeHandler(FIELDS.EMAIL, setEmail)}
                required={true}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                value={startDate ? moment(startDate, DateFormats.DAY, true) : null}
                label={FIELDS.START_DATE.label}
                onChange={handleStartDateChange}
                format={DateFormats.DAY}
                disablePast
                slotProps={{
                  textField: {
                    fullWidth: true,
                    name: FIELDS.START_DATE.name,
                    helperText: getHelperText(FIELDS.START_DATE, startDate),
                    error: isValid(FIELDS.START_DATE, startDate),
                    required: true,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={isValid(FIELDS.DURATION, duration)} required>
                <InputLabel id="duration-label">{FIELDS.DURATION.label}</InputLabel>
                <Select
                  labelId="duration-label"
                  name={FIELDS.DURATION.name}
                  value={duration}
                  label={FIELDS.DURATION.label}
                  onChange={constructChangeHandler(FIELDS.DURATION, setDuration)}
                >
                  {durationOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{getHelperText(FIELDS.DURATION, duration)}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                error={isValid(FIELDS.EQUIPMENT_TYPES, equipmentTypes.length)}
                required
              >
                <InputLabel id="equipment-types-label">{FIELDS.EQUIPMENT_TYPES.label}</InputLabel>
                <Select
                  labelId="equipment-types-label"
                  id="equipment-types"
                  multiple
                  name={FIELDS.EQUIPMENT_TYPES.name}
                  value={equipmentTypes}
                  label={FIELDS.EQUIPMENT_TYPES.label}
                  onChange={constructChangeHandler(FIELDS.EQUIPMENT_TYPES, setEquipmentTypes)}
                  renderValue={(selected) => (
                    <Typography>{`${(selected as string[]).length} selected`}</Typography>
                  )}
                >
                  {equipmentTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox checked={equipmentTypes.includes(option.value)} />
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontSize={13}>
                            {option.label}
                          </Typography>
                        }
                      />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {getHelperText(FIELDS.EQUIPMENT_TYPES, equipmentTypes.length)}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={disableSubmit || submitting || submitted}
                  sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 220 }, py: 1.25 }}
                >
                  {submitted ? 'Request Sent' : 'Submit Request'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </LocalizationProvider>
    </RequestLocationFormCard>
  )
}

export default RequestLocationForm;
